import { createApi } from '@reduxjs/toolkit/query/react';
import { portalBaseQuery } from '@lib/baseQuery';
import { services } from '@config/services';
import type {
  ShippingMappingOverrideItem,
  ShippingMappingOverridesArgs,
  ShippingMappingOverridesBulkBody,
  ShippingMappingOverridesBulkResponse,
  ShippingMappingOverridesResponse,
} from './types';

/**
 * Merchant shipping-mapping-overrides slice. Drives the
 * ConnectionDetails > Shipping Mapping sub-tab (Phase 4 of the
 * Plugins port).
 *
 * Live wire shapes (verified 2026-05-28 as super-admin viewing
 * Sales Channel 1 on the Julia A Vendor Plugin connection details):
 *
 *   GET /merchant/v1/shipping-mapping-overrides
 *     ?merchantId=<uuid>&vendorId=<uuid>&page=1&pageSize=250
 *     → { items: ShippingMappingOverrideItem[] }
 *
 *   POST /merchant/v1/shipping-mapping-overrides/bulk
 *     body: { bulk: ShippingMappingOverrideItem[] }
 *     → { bulk: [{ status, response }, ...] } (per-row)
 *
 *   DELETE /merchant/v1/shipping-mapping-overrides/{id}
 *     (legacy has no bulk-delete; the upload flow deletes existing
 *     overrides one at a time before posting the new bulk body —
 *     atomic-replace semantics surfaced via the `replace` mutation
 *     below)
 */
export const merchantShippingMappingsApi = createApi({
  reducerPath: 'merchantShippingMappingsApi',
  baseQuery: portalBaseQuery,
  tagTypes: ['ShippingMappingOverride'],
  endpoints: (builder) => ({
    /**
     * Live URL captured 2026-05-28:
     *   GET /merchant/v1/shipping-mapping-overrides
     *     ?merchantId=f8bebda7-…&vendorId=ee667059-…&page=1&pageSize=250
     *
     * `facilityId` is optional — the legacy service helper appends it
     * when provided. Param order is preserved verbatim from the
     * legacy `getShippingMappingOverride` helper so the wire matches.
     */
    getShippingMappingOverrides: builder.query<
      ShippingMappingOverridesResponse,
      ShippingMappingOverridesArgs
    >({
      query: ({ merchantId, vendorId, facilityId, page = 1, pageSize = 250 }) => {
        const params = new URLSearchParams();
        params.set('merchantId', merchantId);
        params.set('vendorId', vendorId);
        if (facilityId) params.set('facilityId', facilityId);
        params.set('page', String(page));
        params.set('pageSize', String(pageSize));
        return {
          url: `${services.merchant}/v1/shipping-mapping-overrides?${params.toString()}`,
          method: 'GET',
        };
      },
      transformResponse: (
        response: ShippingMappingOverridesResponse | { items?: ShippingMappingOverridesResponse['items'] },
      ): ShippingMappingOverridesResponse => ({
        items: response?.items ?? [],
        total: (response as ShippingMappingOverridesResponse)?.total,
        page: (response as ShippingMappingOverridesResponse)?.page,
        pageSize: (response as ShippingMappingOverridesResponse)?.pageSize,
      }),
      providesTags: (_r, _e, { merchantId, vendorId }) => [
        { type: 'ShippingMappingOverride', id: `${merchantId}:${vendorId}` },
      ],
    }),

    /**
     * Bulk create — POST /merchant/v1/shipping-mapping-overrides/bulk
     * Body shape: { bulk: ShippingMappingOverrideItem[] }
     * Response shape: { bulk: [{ status, response }, ...] }
     *
     * The bulk endpoint does NOT delete existing rows; callers that
     * want replace semantics use `replaceShippingMappingOverrides`
     * below, which delete-then-bulk-create matching portalnew's
     * `uploadShippingMappings` saga.
     */
    createShippingMappingOverridesBulk: builder.mutation<
      ShippingMappingOverridesBulkResponse,
      ShippingMappingOverridesBulkBody
    >({
      query: (body) => ({
        url: `${services.merchant}/v1/shipping-mapping-overrides/bulk`,
        method: 'POST',
        body,
      }),
      // Match the query's provided tag (`${merchantId}:${vendorId}`) — a
      // type-level invalidation covers every cached merchant:vendor entry.
      // The previous `id: 'LIST'` matched nothing the query provided, so the
      // overrides list went stale after create/delete.
      invalidatesTags: ['ShippingMappingOverride'],
    }),

    /**
     * Delete one override row. There is no bulk-delete endpoint on the
     * server; the upload flow deletes pre-existing rows one at a time
     * inside `replaceShippingMappingOverrides`.
     */
    deleteShippingMappingOverride: builder.mutation<void, string>({
      query: (id) => ({
        url: `${services.merchant}/v1/shipping-mapping-overrides/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
      // Match the query's provided tag (`${merchantId}:${vendorId}`) — a
      // type-level invalidation covers every cached merchant:vendor entry.
      // The previous `id: 'LIST'` matched nothing the query provided, so the
      // overrides list went stale after create/delete.
      invalidatesTags: ['ShippingMappingOverride'],
    }),

    /**
     * Update one override in place — atomic single call.
     *
     * Live wire shape (verified against merchant-service controller):
     *   PUT /merchant/v1/shipping-mapping-overrides
     *   body: { id, merchantId, vendorId, facilityId, shippingGroupId, shippingMappings }
     *   → ShippingMappingOverridesDto (200)
     *
     * Used by handleGroupChange to change shippingGroupId without the
     * data-loss risk of delete-then-create.
     */
    updateShippingMappingOverride: builder.mutation<
      ShippingMappingOverridesResponse,
      ShippingMappingOverrideItem & { id: string }
    >({
      query: (item) => ({
        url: `${services.merchant}/v1/shipping-mapping-overrides`,
        method: 'PUT',
        body: item,
      }),
      invalidatesTags: ['ShippingMappingOverride'],
    }),
  }),
});

export const {
  useGetShippingMappingOverridesQuery,
  useCreateShippingMappingOverridesBulkMutation,
  useDeleteShippingMappingOverrideMutation,
  useUpdateShippingMappingOverrideMutation,
} = merchantShippingMappingsApi;

/**
 * Replace-semantics helper. portalnew's `uploadShippingMappings` saga
 * deletes all pre-existing override ids one-at-a-time, then POSTs the
 * new bulk body — net effect: a full replace. RTK Query mutations
 * don't compose, so callers run this as a regular function that wraps
 * the two underlying mutations and returns the bulk response.
 *
 * Usage:
 *   const [delMut] = useDeleteShippingMappingOverrideMutation();
 *   const [createMut] = useCreateShippingMappingOverridesBulkMutation();
 *   const resp = await replaceShippingMappingOverrides({
 *     existingIds: ['...', '...'],
 *     body: { bulk: [...] },
 *     deleteOne: (id) => delMut(id).unwrap(),
 *     createBulk: (body) => createMut(body).unwrap(),
 *   });
 */
export interface ReplaceShippingMappingOverridesArgs {
  existingIds: string[];
  body: ShippingMappingOverridesBulkBody;
  deleteOne: (id: string) => Promise<unknown>;
  createBulk: (body: ShippingMappingOverridesBulkBody) => Promise<ShippingMappingOverridesBulkResponse>;
}

export async function replaceShippingMappingOverrides(
  args: ReplaceShippingMappingOverridesArgs,
): Promise<ShippingMappingOverridesBulkResponse> {
  if (args.existingIds.length > 0) {
    // legacy deletes one-by-one via Promise.all
    await Promise.all(args.existingIds.map((id) => args.deleteOne(id)));
  }
  return args.createBulk(args.body);
}
